import { TranslationsProvider, useTranslation } from "@kamstrup/flow-core/localization";
import {
    SearchIconButton,
    useGlobalSearchOverlay
} from "@kamstrup/flow-ui/globalsearch";
import { flowUITranslations } from "@kamstrup/flow-ui/localization";
import { FlowTopBar, AppModules, type MenuItems } from "@kamstrup/flow-ui/topbar";
import { useAuth } from "@kamstrup/flow-core/security";
import { Link } from "@mui/material";
import { FC, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FeatureToggleKey } from "src/api";
import { useFeatureToggle } from "src/api/featureToggling/useGetFeatureToggle";
import { useFlowApps } from "src/hooks/useFlowApps";
import { getServices } from "src/logs";

export const TopBarContent: FC = () => {
  const navigate = useNavigate();

  const { open: openSearch } = useGlobalSearchOverlay();
  const { data: showGlobalSearch } = useFeatureToggle(FeatureToggleKey.showGlobalSearch);
  const auth = useAuth();

  const { translateString } = useTranslation();
  const { data: availableFlowApps } = useFlowApps();

  const menuItems: MenuItems[] = useMemo(
    () =>
      availableFlowApps?.map((app) => ({
        label: translateString({ id: app.label, defaultMessage: app.defaultMessage }),
        url: app.url,
        key: app.key.split("-")[1]
      })) || [],
    [availableFlowApps, translateString]
  );

  const topBarLinks = useMemo(
    () => ({ dashboardUrl: `${window.location.origin}/dashboard`, menuItems }),
    [menuItems]
  );

  const userInfo = auth?.instance?.getActiveAccount()?.idTokenClaims || {};

  const handleSignOut = useCallback(() => {
    getServices().authService.logout();
  }, []);

  const handleSettings = () => {
    navigate("/settings");
  };

  const userMenu = {
    name: userInfo.tenant_name as string,
    userName: userInfo.user_name as string,
    signOutText: translateString({
      id: "topBar.menu.signOut.button",
      defaultMessage: "Sign Out"
    }),
    handleSignOut,
    settingsText: translateString({
      id: "topBar.menu.settings",
      defaultMessage: "Settings"
    }),
    handleSettings
  };

  return (
    <TranslationsProvider translations={flowUITranslations} developerMessagesFallback={flowUITranslations.dev}>
    <FlowTopBar
      topBarLinks={topBarLinks}
      activeModuleKey={AppModules.dashboard}
      linkComponent={Link}
      userMenu={userMenu}
      rightSlot={
        showGlobalSearch?.isEnabled ? <SearchIconButton onClick={openSearch} /> : undefined
      }
    />
    </TranslationsProvider>
  );
};
